import React from 'react';
import { motion } from 'framer-motion';

export default function About() {
  return (
    <main className="pt-32 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-16 items-center mb-32">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-4xl md:text-6xl text-linen mb-8 leading-tight">
              Embracing the <br/><span className="italic text-ember">Shadows</span>
            </h1>
            <div className="space-y-6 text-linen-dark text-lg leading-relaxed">
              <p>
                EmberVale was born from a desire to create fragrances that don't just smell pleasant, but evoke emotion, memory, and atmosphere. We reject the overtly sweet and predictable in favor of the complex, the earthy, and the moody.
              </p>
              <p>
                Founded in a small, dimly lit studio, our process is inherently slow and deliberate. We see candle making not as manufacturing, but as an art form—a way to bottle the essence of a midnight forest, an old library, or a dying fire.
              </p>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="aspect-[4/5] bg-smoke-light relative"
          >
            <picture>
              <source srcSet="https://images.unsplash.com/photo-1617897903246-719242758050?auto=format,compress&fit=crop&q=80&w=800&fm=webp" type="image/webp" />
              <img 
                src="https://images.unsplash.com/photo-1617897903246-719242758050?auto=format,compress&fit=crop&q=80&w=800" 
                alt="Close-up of molten organic coconut wax being poured into a vessel" 
                width="800"
                height="1000"
                loading="eager"
                decoding="async"
                className="w-full h-full object-cover opacity-80 mix-blend-luminosity" 
              />
            </picture>
            <div className="absolute inset-0 border border-white/10 m-4 pointer-events-none"></div>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="aspect-square bg-smoke-light relative order-2 md:order-1"
          >
            <picture>
              <source srcSet="https://images.unsplash.com/photo-1599839619722-39751411ea63?auto=format,compress&fit=crop&q=80&w=800&fm=webp" type="image/webp" />
              <img 
                src="https://images.unsplash.com/photo-1599839619722-39751411ea63?auto=format,compress&fit=crop&q=80&w=800" 
                alt="Still life of botanical ingredients including dried cedar and peppercorns" 
                width="800"
                height="800"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover opacity-80 mix-blend-luminosity" 
              />
            </picture>
            <div className="absolute bottom-6 left-6 right-6">
              <span className="text-[9px] uppercase tracking-[0.4em] text-linen/30 font-bold">Fragrance Note Still Life</span>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="order-1 md:order-2"
          >
            <h2 className="text-xs uppercase tracking-widest text-ember font-semibold mb-6">Our Ingredients</h2>
            <h3 className="font-serif text-3xl md:text-4xl text-linen mb-6">Uncompromising Quality</h3>
            <div className="space-y-6 text-linen-dark leading-relaxed">
              <p>
                We use a custom blend of coconut and apricot wax, chosen for its clean burn and exceptional ability to hold fragrance. Our wicks are 100% cotton, lead-free, and carefully sized for each specific vessel and scent profile.
              </p>
              <p>
                Our fragrances are complex blends of fine fragrance oils and essential oils, rigorously tested to ensure they are free from phthalates, parabens, and other harmful toxins. We believe luxury should never come at the cost of your well-being.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </main>
  );
}